export const SEVEN_SEGMENT_DIGIT_COUNT_OPTIONS = Object.freeze([
  { label: '1 Digit', value: 1 },
  { label: '2 Digits', value: 2 },
  { label: '3 Digits', value: 3 },
  { label: '4 Digits', value: 4 },
]);

export const SEVEN_SEGMENT_COMMON_TYPE_OPTIONS = Object.freeze([
  { label: 'Common Cathode', value: 'common_cathode' },
  { label: 'Common Anode', value: 'common_anode' },
]);

export const SEVEN_SEGMENT_SEGMENT_PIN_KEYS = Object.freeze([
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'DP',
]);

export const SEVEN_SEGMENT_DIGIT_PIN_KEYS = Object.freeze([
  'DIG1',
  'DIG2',
  'DIG3',
  'DIG4',
]);

export const SEVEN_SEGMENT_PIN_KEYS = Object.freeze([
  ...SEVEN_SEGMENT_SEGMENT_PIN_KEYS,
  ...SEVEN_SEGMENT_DIGIT_PIN_KEYS,
]);

export const SEVEN_SEGMENT_SEGMENT_KEYS = Object.freeze([
  'a',
  'b',
  'c',
  'd',
  'e',
  'f',
  'g',
  'dp',
]);

export const SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT = 4;
export const SEVEN_SEGMENT_MAX_DIGIT_COUNT = 4;
export const SEVEN_SEGMENT_DEFAULT_COMMON_TYPE = 'common_cathode';

export const SEVEN_SEGMENT_SEGMENT_PIN_KEY_BY_SEGMENT = Object.freeze({
  a: 'A',
  b: 'B',
  c: 'C',
  d: 'D',
  e: 'E',
  f: 'F',
  g: 'G',
  dp: 'DP',
});

export const SEVEN_SEGMENT_SEGMENT_KEY_BY_PIN = Object.freeze(
  Object.fromEntries(
    Object.entries(SEVEN_SEGMENT_SEGMENT_PIN_KEY_BY_SEGMENT)
      .map(([segmentKey, pinKey]) => [pinKey, segmentKey])
  )
);

export const SEVEN_SEGMENT_RENDER_LAYOUT = Object.freeze({
  bodyHeight: 49.774,
  digitWidth: 34.632,
  footerHeight: 27.2,
  minimumWidth: 84,
  modulePaddingX: 4,
  totalHeight: 78,
  digitPitch: 29,
});

export const normalizeSevenSegmentDigitCount = (
  value,
  fallback = SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT
) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  const roundedValue = Math.round(numericValue);
  return Math.min(SEVEN_SEGMENT_MAX_DIGIT_COUNT, Math.max(1, roundedValue));
};

export const normalizeSevenSegmentCommonType = (
  value,
  fallback = SEVEN_SEGMENT_DEFAULT_COMMON_TYPE
) => {
  const normalizedValue = String(value || '').trim().toLowerCase();

  if (normalizedValue === 'common_anode' || normalizedValue === 'common cathode') {
    return normalizedValue === 'common cathode'
      ? 'common_cathode'
      : 'common_anode';
  }

  if (normalizedValue === 'common_cathode' || normalizedValue === 'common anode') {
    return normalizedValue === 'common anode'
      ? 'common_anode'
      : 'common_cathode';
  }

  return fallback;
};

export const getSevenSegmentCommonTypeLabel = (commonType) => (
  SEVEN_SEGMENT_COMMON_TYPE_OPTIONS.find(
    (option) => option.value === normalizeSevenSegmentCommonType(commonType)
  )?.label || 'Common Cathode'
);

export const getSevenSegmentDigitPinKeys = (digitCount) => {
  const safeDigitCount = normalizeSevenSegmentDigitCount(digitCount);
  return SEVEN_SEGMENT_DIGIT_PIN_KEYS.slice(0, safeDigitCount);
};

export const getSevenSegmentComponentPinKeys = (digitCount) => ([
  ...SEVEN_SEGMENT_SEGMENT_PIN_KEYS,
  ...getSevenSegmentDigitPinKeys(digitCount),
]);

export const getSevenSegmentSegmentPinKey = (segmentKey) => (
  SEVEN_SEGMENT_SEGMENT_PIN_KEY_BY_SEGMENT[segmentKey] || ''
);

export const getSevenSegmentSegmentKeyByPin = (pinKey) => (
  SEVEN_SEGMENT_SEGMENT_KEY_BY_PIN[String(pinKey || '').trim().toUpperCase()] || ''
);

export const createSevenSegmentDigitState = (overrides = {}) => (
  Object.fromEntries(
    SEVEN_SEGMENT_SEGMENT_KEYS.map((segmentKey) => [
      segmentKey,
      Boolean(overrides?.[segmentKey]),
    ])
  )
);

export const createSevenSegmentFigureEightState = () => (
  createSevenSegmentDigitState({
    a: true,
    b: true,
    c: true,
    d: true,
    e: true,
    f: true,
    g: true,
    dp: false,
  })
);

export const buildSevenSegmentSegmentStates = (
  digitCount,
  { mode = 'off' } = {}
) => {
  const safeDigitCount = normalizeSevenSegmentDigitCount(digitCount);
  const digitStateFactory = mode === 'figure8'
    ? createSevenSegmentFigureEightState
    : createSevenSegmentDigitState;

  return Array.from({ length: safeDigitCount }, () => digitStateFactory());
};

export const normalizeSevenSegmentSegmentStates = (
  value,
  digitCount,
  { fallbackMode = 'off' } = {}
) => {
  const safeDigitCount = normalizeSevenSegmentDigitCount(digitCount);
  const fallbackStates = buildSevenSegmentSegmentStates(safeDigitCount, {
    mode: fallbackMode,
  });

  if (!Array.isArray(value)) {
    return fallbackStates;
  }

  return Array.from({ length: safeDigitCount }, (_, index) => (
    createSevenSegmentDigitState(value[index] || fallbackStates[index] || {})
  ));
};

export const getSevenSegmentDisplayCanvasSize = (digitCount) => {
  const safeDigitCount = normalizeSevenSegmentDigitCount(digitCount);
  const digitStripWidth = (
    SEVEN_SEGMENT_RENDER_LAYOUT.digitWidth +
    ((safeDigitCount - 1) * SEVEN_SEGMENT_RENDER_LAYOUT.digitPitch)
  );
  const width = Math.max(
    SEVEN_SEGMENT_RENDER_LAYOUT.minimumWidth,
    SEVEN_SEGMENT_RENDER_LAYOUT.modulePaddingX * 2 +
    digitStripWidth
  );

  return {
    width,
    height: SEVEN_SEGMENT_RENDER_LAYOUT.totalHeight,
  };
};

export const resolveSevenSegmentCanvasSize = (
  digitCount,
  {
    baseCanvasWidth,
    baseCanvasHeight,
    baseDigitCount = SEVEN_SEGMENT_DEFAULT_DIGIT_COUNT,
  } = {}
) => {
  const resolvedBaseDigitCount = normalizeSevenSegmentDigitCount(baseDigitCount);
  const intrinsicBaseSize = getSevenSegmentDisplayCanvasSize(resolvedBaseDigitCount);
  const intrinsicTargetSize = getSevenSegmentDisplayCanvasSize(digitCount);
  const numericBaseCanvasWidth = Number(baseCanvasWidth);
  const numericBaseCanvasHeight = Number(baseCanvasHeight);
  const widthScale = (
    Number.isFinite(numericBaseCanvasWidth) &&
    numericBaseCanvasWidth > 0 &&
    intrinsicBaseSize.width > 0
  )
    ? numericBaseCanvasWidth / intrinsicBaseSize.width
    : 1;
  const heightScale = (
    Number.isFinite(numericBaseCanvasHeight) &&
    numericBaseCanvasHeight > 0 &&
    intrinsicBaseSize.height > 0
  )
    ? numericBaseCanvasHeight / intrinsicBaseSize.height
    : 1;

  return {
    width: Math.round(intrinsicTargetSize.width * widthScale * 100) / 100,
    height: Math.round(intrinsicTargetSize.height * heightScale * 100) / 100,
  };
};
